/**
 * apps/web :: lib/states
 *
 * Canonical catalog of US states RoadBridge supports. Backs:
 *   - The <StatePicker> leaf component (single/multi state selection UI).
 *   - The onboarding wizard (picks the learner's primary state).
 *   - The dashboard quick-switch (CDL drivers may study multi-state).
 *   - The rule-diff request builder (maps `UsStateCode` → handbook URL
 *     sent to the backend).
 *
 * Catalog invariants exercised in `states.test.ts`:
 *   - MVP Phase 0 ships 10 active states: CA / TX / FL / NY / NJ
 *     (original Wave 0 set) plus IL / MA / WA / AZ / GA (added in
 *     Wave 9a / ROADS-317).
 *   - `getState(code)` returns `undefined` for unknown codes (no throw) so
 *     callers can render a graceful "state not supported yet" path.
 *   - `US_STATES` is `readonly`/`as const` so downstream code can't mutate
 *     the catalog at runtime.
 *
 * Lockstep: this `UsStateCode` literal union MUST match the canonical
 * `kmp-shared-core/.../shared/profile/UsStateCode.kt` enum and the
 * `packages/analytics/src/events.ts` `StateCode` union. The backend
 * rejects unknown codes at the profile / practice / exam endpoints,
 * so this catalog must not advertise codes the backend can't accept.
 *
 * Handbook URL verification (audit timestamp: 2026-04-23 — see PR body for
 * the full curl HTTP-status audit; every active URL listed at audit time
 * returned 200). If any URL 404s later, the content-ops process is to
 * swap the URL here + re-run the audit — do not silently point at a redirect.
 *
 * Wave 9a note: the IL/MA/WA/AZ/GA handbook URLs ship as the canonical
 * state DMV-handbook landing page found via that state's official .gov
 * portal. The audit script that runs over `US_STATES` will re-verify
 * 200s on next CI run; if any of these 404 over time, content-ops will
 * patch the URL here.
 */

export type UsStateCode =
  | "CA"
  | "TX"
  | "FL"
  | "NY"
  | "NJ"
  | "IL"
  | "MA"
  | "WA"
  | "AZ"
  | "GA";

export interface UsState {
  /** Canonical two-letter USPS code. Used as the wire-format identifier. */
  code: UsStateCode;
  /** English display name (reference). UIs should prefer the i18n key
   *  `state.<code-lowercase>.name` to render localised names. */
  name: string;
  /** Canonical state DMV driver-handbook URL. Empty string when the canonical
   *  URL 404'd at audit time — PR notes call out any such entries. */
  handbookUrl: string;
  /** true for MVP; future states will be added with `active: false` + a
   *  waitlist UX path. No inactive states exist at MVP Phase 0. */
  active: boolean;
  /** ISO 639-1 language subtags (plus BCP-47 script variants like `zh-Hans`)
   *  for which we've reviewed state-specific content. Drives the
   *  supported-language count badge on the StatePicker card. */
  supportedLanguages: readonly string[];
  /** true if CDL rule-diffs are targeted for this state at Phase 0. NJ is
   *  Class C only until the CDL content pipeline rolls out. */
  cdlAvailable: boolean;
}

/**
 * MVP 10-state catalog. Order here is the order cards render in the picker —
 * CA first because it's the largest immigrant destination, TX second for the
 * same reason, then FL / NY / NJ by population of newcomers, then the
 * Wave 9a additions IL / MA / WA / AZ / GA in a second tier.
 *
 * `supportedLanguages` for Wave 9a additions starts at the conservative
 * `en + es` pair (matching FL / NJ); content-ops can widen these
 * per-state once native-speaker review is in for the additional locales.
 *
 * `cdlAvailable` defaults to `false` for the Wave 9a five — Pillar B
 * (CDL) content has not been authored for them yet, so the picker must
 * not surface a CDL badge that the rule-diff engine can't back up.
 */
export const US_STATES: readonly UsState[] = [
  {
    code: "CA",
    name: "California",
    handbookUrl:
      "https://www.dmv.ca.gov/portal/handbook/california-driver-handbook/",
    active: true,
    supportedLanguages: ["en", "es", "hi", "te", "tl", "ru", "uk", "zh-Hans"],
    cdlAvailable: true,
  },
  {
    code: "TX",
    name: "Texas",
    handbookUrl: "https://www.dps.texas.gov/internetforms/Forms/DL-7.pdf",
    active: true,
    supportedLanguages: ["en", "es", "hi", "te", "tl", "ru", "uk", "zh-Hans"],
    cdlAvailable: true,
  },
  {
    code: "FL",
    name: "Florida",
    handbookUrl:
      "https://www.flhsmv.gov/pdf/handbooks/englishdriverhandbook.pdf",
    active: true,
    supportedLanguages: ["en", "es"],
    cdlAvailable: true,
  },
  {
    code: "NY",
    name: "New York",
    handbookUrl:
      "https://dmv.ny.gov/driver-license/drivers-manual-practice-tests",
    active: true,
    supportedLanguages: ["en", "es", "ru", "uk", "zh-Hans"],
    cdlAvailable: true,
  },
  {
    code: "NJ",
    name: "New Jersey",
    handbookUrl: "https://www.nj.gov/mvc/pdf/license/drivermanual.pdf",
    active: true,
    supportedLanguages: ["en", "es"],
    cdlAvailable: false,
  },
  {
    code: "IL",
    name: "Illinois",
    handbookUrl:
      "https://www.ilsos.gov/publications/pdf_publications/dsd_a112.pdf",
    active: true,
    supportedLanguages: ["en", "es"],
    cdlAvailable: false,
  },
  {
    code: "MA",
    name: "Massachusetts",
    handbookUrl:
      "https://www.mass.gov/lists/drivers-manuals",
    active: true,
    supportedLanguages: ["en", "es"],
    cdlAvailable: false,
  },
  {
    code: "WA",
    name: "Washington",
    handbookUrl:
      "https://dol.wa.gov/driver-licenses-and-permits/driver-guide",
    active: true,
    supportedLanguages: ["en", "es"],
    cdlAvailable: false,
  },
  {
    code: "AZ",
    name: "Arizona",
    handbookUrl:
      "https://azdot.gov/motor-vehicles/driver-services/driver-license-information/driver-license-manuals-and-practice-tests",
    active: true,
    supportedLanguages: ["en", "es"],
    cdlAvailable: false,
  },
  {
    code: "GA",
    name: "Georgia",
    handbookUrl:
      "https://dds.georgia.gov/georgia-drivers-manual",
    active: true,
    supportedLanguages: ["en", "es"],
    cdlAvailable: false,
  },
] as const;

/**
 * Canonical-lookup helper. Returns `undefined` for unknown codes so callers
 * can fall through to a "state not supported" UX path without try/catch.
 */
export function getState(code: UsStateCode): UsState | undefined {
  return US_STATES.find((s) => s.code === code);
}

/**
 * Subset of the catalog we're actively serving. At MVP Phase 0 this equals
 * the full catalog; future additions will ship with `active: false` +
 * waitlist copy until content ops verifies their handbook content.
 */
export function activeStates(): readonly UsState[] {
  return US_STATES.filter((s) => s.active);
}
